import type { ColumnDef } from "@tanstack/react-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import type { Material } from "@/types/material";

/**
 * Column definitions used exclusively for Excel/CSV export.
 * Work directly on the raw Material API type — no mapping step.
 *
 * meta.label  → Excel column header
 * accessorKey → raw field value
 * accessorFn  → computed/formatted value
 */
export function getIngredientExportColumns(i18n: I18n): ColumnDef<Material, unknown>[] {
  return [
    {
      id: "id",
      accessorKey: "id",
      meta: { label: i18n._(msg`ID`) },
    },
    {
      id: "name",
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
    },
    {
      id: "sku",
      accessorKey: "sku",
      meta: { label: i18n._(msg`Code`) },
    },
    {
      id: "type",
      accessorKey: "type",
      meta: { label: i18n._(msg`Type`) },
    },
    {
      id: "unitOfMeasure",
      accessorKey: "unitOfMeasure",
      meta: { label: i18n._(msg`Unit of Measure`) },
    },
    {
      id: "unitCost",
      accessorKey: "unitCost",
      meta: { label: i18n._(msg`Unit Cost`) },
    },
    {
      id: "unitCostUnit",
      accessorKey: "unitCostUnit",
      meta: { label: i18n._(msg`Cost Unit`) },
    },
    {
      id: "caloriesPerUnit",
      accessorKey: "caloriesPerUnit",
      meta: { label: i18n._(msg`Calories / Unit`) },
    },
    {
      id: "proteinG",
      accessorKey: "proteinG",
      meta: { label: i18n._(msg`Protein (g)`) },
    },
    {
      id: "carbsG",
      accessorKey: "carbsG",
      meta: { label: i18n._(msg`Carbs (g)`) },
    },
    {
      id: "fatG",
      accessorKey: "fatG",
      meta: { label: i18n._(msg`Fat (g)`) },
    },
    {
      id: "fiberG",
      accessorKey: "fiberG",
      meta: { label: i18n._(msg`Fiber (g)`) },
    },
    {
      id: "sodiumMg",
      accessorKey: "sodiumMg",
      meta: { label: i18n._(msg`Sodium (mg)`) },
    },
    {
      id: "sugarG",
      accessorKey: "sugarG",
      meta: { label: i18n._(msg`Sugar (g)`) },
    },
    {
      id: "allergens",
      accessorFn: (row) =>
        (row.allergens ?? []).map((a) => a.allergen.code).join(", "),
      meta: { label: i18n._(msg`Allergens`) },
    },
    {
      id: "description",
      accessorKey: "description",
      meta: { label: i18n._(msg`Description`) },
    },
    {
      id: "imageUrl",
      accessorKey: "imageUrl",
      meta: { label: i18n._(msg`Image URL`) },
    },
    {
      id: "isActive",
      accessorFn: (row) => (row.isActive ? i18n._(msg`Active`) : i18n._(msg`Inactive`)),
      meta: { label: i18n._(msg`Status`) },
    },
  ];
}
